import { Link } from "react-router";
import type { BlockContentMap } from "@veo/types";
import { cn } from "@veo/ui";
import { RichText } from "../components/RichText";
import { ResponsiveImage } from "../components/ResponsiveImage";
import { Reveal } from "../components/motion";
import { SectionShell, type SectionProps } from "./SectionRenderer";

const RATIOS: Record<string, string> = {
  square: "aspect-square",
  portrait: "aspect-[3/4]",
  landscape: "aspect-[4/3]",
  auto: "",
};

export default function TextImageBlock({
  content,
  settings,
}: SectionProps<BlockContentMap["textImage"]>) {
  return (
    <SectionShell settings={settings}>
      <div className="grid gap-10 lg:grid-cols-2 lg:items-center lg:gap-16">
        {/*
          Copy first, image a beat behind — reading order, not source order, so
          the delay is on the image whichever side `imagePosition` puts it.
        */}
        <Reveal className={cn(content.imagePosition === "left" && "lg:order-2")}>
          {content.eyebrow && (
            <p className="text-xs font-semibold uppercase tracking-wider text-gold-600">
              {content.eyebrow}
            </p>
          )}
          {content.heading && <h2 className="mt-3 text-4xl">{content.heading}</h2>}
          <div className="mt-6">
            <RichText doc={content.body} />
          </div>

          {content.buttons.length > 0 && (
            <div className="mt-8 flex flex-wrap gap-3">
              {content.buttons.map((button, index) => (
                <Link
                  key={button.href + button.label}
                  to={button.href}
                  className={cn(
                    "rounded-md px-6 py-3 text-sm font-semibold transition-colors",
                    index === 0
                      ? "bg-navy-900 text-cream-50 hover:bg-navy-800"
                      : "text-navy-900 ring-1 ring-inset ring-ink-300 hover:bg-ink-50",
                  )}
                >
                  {button.label}
                </Link>
              ))}
            </div>
          )}
        </Reveal>

        {content.image && (
          <Reveal delay={0.15} className={cn(content.imagePosition === "left" && "lg:order-1")}>
            <ResponsiveImage
              image={content.image}
              sizes="(min-width: 1024px) 50vw, 100vw"
              className={cn(
                "w-full rounded-xl object-cover",
                RATIOS[content.imageRatio] ?? RATIOS.landscape,
              )}
            />
          </Reveal>
        )}
      </div>
    </SectionShell>
  );
}
